import { useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "@/auth/auth-provider";
import { hasCapability } from "@/auth/capabilities";
import { RatioVerification } from "@/components/ratio-verification";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, ApiError, type ControllerDetail, type ControllerMeter, type Feeder } from "@/lib/api";
import { FeederEditDialog } from "@/pages/feeders";

type Hardware = { controller: ControllerDetail; meter: ControllerMeter };
type Action = { kind: "replace" | "detach"; source: Hardware };

function hardwareLabel(hardware: Hardware): string {
  return `${hardware.controller.display_name} (${hardware.controller.device_id}), ${hardware.meter.label || `slot ${hardware.meter.slot}`} (Modbus ${hardware.meter.modbus_id})`;
}

function instant(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "Unknown";
}

export function FeederDetailPage(): JSX.Element {
  const { me } = useAuth();
  const { feederId } = useParams();
  return <FeederDetail key={`${me?.tenant_id ?? "platform"}:${feederId}`} />;
}

function FeederDetail(): JSX.Element {
  const { feederId } = useParams();
  const id = Number(feederId);
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [action, setAction] = useState<Action | null>(null);
  const [targetKey, setTargetKey] = useState("");
  const [effective, setEffective] = useState("");
  const [confirmedMove, setConfirmedMove] = useState(false);
  const [review, setReview] = useState(false);
  const [reviewedTarget, setReviewedTarget] = useState<Hardware | null>(null);
  const [reviewedMove, setReviewedMove] = useState(false);
  const [reviewedPreviousFeeder, setReviewedPreviousFeeder] = useState("");
  const [reviewedFeeder, setReviewedFeeder] = useState<Feeder | null>(null);
  const [controllerSearch, setControllerSearch] = useState("");
  const [controllerPage, setControllerPage] = useState(1);
  const [targetControllerId, setTargetControllerId] = useState("");
  const feederQuery = useQuery({
    queryKey: ["feeder", id],
    queryFn: () => api.feeder(id),
    enabled: Number.isInteger(id) && id > 0,
  });
  const canReadAnalytics = hasCapability(me, "analytics:read");
  const history = useQuery({
    queryKey: ["feeder-history", id],
    queryFn: () => api.feederHistory(id),
    enabled: canReadAnalytics && Number.isInteger(id) && id > 0,
  });
  const canConfigure = hasCapability(me, "controllers:configure");
  const controllers = useQuery({
    queryKey: ["replacement-controllers", controllerSearch, controllerPage],
    queryFn: () =>
      api.browseControllers({ search: controllerSearch, page: controllerPage, page_size: 25 }),
    enabled: canConfigure && action?.kind === "replace",
  });
  const assignmentDevices = Array.from(
    new Set((feederQuery.data?.assignments ?? []).map((assignment) => assignment.device_id)),
  );
  const details = useQueries({
    queries: assignmentDevices.map((deviceId) => ({
      queryKey: ["controller", deviceId],
      queryFn: () => api.controller(deviceId),
      enabled: canConfigure,
    })),
  });
  const ownsController = (controller: ControllerDetail): boolean =>
    hasCapability(me, "catalog:write") ||
    (me?.tenant_id != null && controller.tenant_id === me.tenant_id);
  const sourceHardware: Hardware[] = details.flatMap((query) =>
    query.data && ownsController(query.data)
      ? query.data.meters.map((meter) => ({ controller: query.data!, meter }))
      : [],
  );
  const targetController = useQuery({
    queryKey: ["controller", targetControllerId],
    queryFn: () => api.controller(targetControllerId),
    enabled: action?.kind === "replace" && Boolean(targetControllerId),
  });
  const hardware: Hardware[] =
    targetController.data && ownsController(targetController.data)
      ? targetController.data.meters.map((meter) => ({ controller: targetController.data!, meter }))
      : [];
  const feeder = feederQuery.data;
  const target = hardware.find(
    (item) => `${item.controller.device_id}:${item.meter.controller_meter_id}` === targetKey,
  );
  const previousFeederId =
    target?.meter.enabled && target.meter.source_mode === "feeder" && target.meter.feeder_id !== id
      ? target.meter.feeder_id
      : null;
  const previousFeederQuery = useQuery({
    queryKey: ["feeder", previousFeederId],
    queryFn: () => api.feeder(previousFeederId!),
    enabled: previousFeederId != null,
  });
  const previousFeeder = previousFeederQuery.data;
  const previousKnown =
    previousFeederId == null || typeof previousFeeder?.is_unassigned_placeholder === "boolean";
  const isPlaceholder = previousFeeder?.is_unassigned_placeholder === true;
  const moving = Boolean(
    target?.meter.enabled &&
    target.meter.source_mode === "feeder" &&
    target.meter.feeder_id !== null &&
    target.meter.feeder_id !== id &&
    !isPlaceholder,
  );
  const parsedTime = effective ? new Date(effective) : null;
  const invalidTime = Boolean(
    parsedTime && (Number.isNaN(parsedTime.getTime()) || parsedTime.getTime() > Date.now()),
  );
  const mutation = useMutation({
    mutationFn: async () => {
      if (!action || invalidTime)
        throw new Error("Choose an effective time in the past or use now.");
      const source = {
        source_device_id: action.source.controller.device_id,
        source_meter_id: action.source.meter.controller_meter_id,
        expected_source_updated_at: action.source.meter.updated_at,
        effective_at: parsedTime?.toISOString() ?? null,
      };
      if (action.kind === "detach") return api.detachFeederAssignment(id, source);
      if (!reviewedTarget || !reviewedFeeder || (reviewedMove && !confirmedMove))
        throw new Error("Select and confirm the replacement meter.");
      return api.replaceFeederAssignment(id, {
        ...source,
        expected_feeder_revision: reviewedFeeder.revision,
        target_device_id: reviewedTarget.controller.device_id,
        target_meter_id: reviewedTarget.meter.controller_meter_id,
        expected_target_updated_at: reviewedTarget.meter.updated_at,
        expected_target_feeder_id: reviewedTarget.meter.feeder_id,
        confirm_move: reviewedMove && confirmedMove,
      });
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ["controller"] });
        await queryClient.invalidateQueries({ queryKey: ["feeders"] });
        await queryClient.invalidateQueries({ queryKey: ["feeder"] });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setAction(null);
    },
  });

  function openAction(kind: Action["kind"], source: Hardware): void {
    mutation.reset();
    setTargetKey("");
    setEffective("");
    setConfirmedMove(false);
    setReview(false);
    setReviewedTarget(null);
    setReviewedMove(false);
    setReviewedPreviousFeeder("");
    setReviewedFeeder(null);
    setControllerSearch("");
    setControllerPage(1);
    setTargetControllerId("");
    setAction({ kind, source });
  }

  if (feederQuery.isLoading)
    return (
      <main className="mx-auto max-w-6xl p-4 sm:p-6">
        <p role="status">Loading feeder…</p>
      </main>
    );
  if (feederQuery.error)
    return (
      <main className="mx-auto max-w-6xl p-4 sm:p-6">
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
            <p role="alert">Could not load feeder.</p>
            <Button onClick={() => void feederQuery.refetch()}>Retry</Button>
          </CardContent>
        </Card>
      </main>
    );
  if (!feeder)
    return (
      <main className="mx-auto max-w-6xl p-4 sm:p-6">
        <Card>
          <CardContent className="space-y-3 p-5">
            <p>Feeder not found or not accessible.</p>
            <Button asChild variant="outline">
              <Link to="/feeders">Back to feeders</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );

  return (
    <main className="mx-auto max-w-6xl space-y-5 p-4 sm:space-y-6 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" className="-ml-3 min-h-11">
          <Link to="/feeders">← Back to feeders</Link>
        </Button>
        <Badge variant="outline" className="font-mono text-xs">
          Feeder #{feeder.feeder_id}
        </Badge>
      </div>
      <PageHeader
        title={feeder.feeder_name}
        description={`${feeder.distribution_company_name} · ${feeder.substation_name}`}
        actions={
          <Badge variant="secondary" className="max-w-full break-all font-mono">
            {feeder.external_key}
          </Badge>
        }
      />
      <div className="grid gap-5 xl:grid-cols-2">
        <Card>
          <section aria-labelledby="feeder-assignments-heading">
            <CardHeader>
              <CardTitle id="feeder-assignments-heading" className="text-base">
                Current assignments
              </CardTitle>
              <CardDescription>
                Connected meters retain this feeder’s stable identity.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {feeder.assignments.length === 0 ? (
                <p
                  role="status"
                  className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
                >
                  No meters are assigned to this feeder.
                </p>
              ) : (
                feeder.assignments.map((assignment) => {
                  const source = sourceHardware.find(
                    (item) =>
                      item.controller.device_id === assignment.device_id &&
                      item.meter.controller_meter_id === assignment.controller_meter_id,
                  );
                  return (
                    <div
                      key={`${assignment.device_id}:${assignment.controller_meter_id}`}
                      className="rounded-lg border bg-background p-4"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 space-y-1">
                          <p className="font-medium">
                            {assignment.meter_label || `Slot ${assignment.slot}`}{" "}
                            <span className="text-muted-foreground">
                              · Modbus {assignment.modbus_id}
                            </span>
                          </p>
                          <p className="break-words text-sm text-muted-foreground">
                            {assignment.controller_display_name} ·{" "}
                            <span className="font-mono text-xs">{assignment.device_id}</span>
                          </p>
                        </div>
                        <Badge variant="outline" className="w-fit shrink-0">
                          {assignment.tenant_name}
                        </Badge>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {hasCapability(me, "controllers:read") ? (
                          <Button asChild variant="link" className="h-11 justify-start px-0">
                            <Link
                              to={`/controllers/${encodeURIComponent(assignment.device_id)}/meters/${assignment.modbus_id}`}
                            >
                              View meter telemetry
                            </Link>
                          </Button>
                        ) : null}
                        {source ? (
                          <>
                            <Button
                              variant="outline"
                              className="min-h-11"
                              onClick={() => openAction("replace", source)}
                            >
                              Replace
                            </Button>
                            <Button
                              variant="outline"
                              className="min-h-11"
                              onClick={() => openAction("detach", source)}
                            >
                              Detach
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
              {canConfigure && details.some((detail) => detail.error) ? (
                <p
                  role="alert"
                  className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm"
                >
                  Could not load controller details. Assignment actions are unavailable for
                  controllers that could not be loaded.
                </p>
              ) : null}
            </CardContent>
          </section>
        </Card>
        <Card>
          <section aria-labelledby="feeder-history-heading">
            <CardHeader>
              <CardTitle id="feeder-history-heading" className="text-base">
                Assignment timeline
              </CardTitle>
              <CardDescription>
                Earlier history may be unknown. Times use your local timezone. Historical access
                follows current controller permissions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {!canReadAnalytics ? (
                <p
                  role="status"
                  className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
                >
                  History requires analytics access.
                </p>
              ) : history.isLoading ? (
                <p
                  role="status"
                  className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
                >
                  Loading history…
                </p>
              ) : history.error ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
                  <p role="alert" className="text-sm">
                    Could not load history.
                  </p>
                  <Button
                    variant="outline"
                    className="min-h-11"
                    onClick={() => void history.refetch()}
                  >
                    Retry
                  </Button>
                </div>
              ) : !history.data?.length ? (
                <p
                  role="status"
                  aria-label="No verified intervals available. Earlier history is unknown."
                  className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
                >
                  No verified intervals available. Earlier history is unknown.
                </p>
              ) : (
                history.data.map((interval, index) => (
                  <article
                    key={`${interval.controller_id}:${interval.meter_id}:${interval.valid_from}:${index}`}
                    className="rounded-lg border bg-background p-4"
                  >
                    <p className="font-medium">
                      Controller {interval.controller_id}{" "}
                      <span className="text-muted-foreground">· Meter {interval.meter_id}</span>
                    </p>
                    <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                      {interval.device_id}
                    </p>
                    <dl className="mt-4 grid gap-x-4 gap-y-3 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="text-xs text-muted-foreground">Effective period</dt>
                        <dd className="mt-1">
                          {instant(interval.valid_from)} to{" "}
                          {interval.valid_to ? instant(interval.valid_to) : "Current"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Recorded</dt>
                        <dd className="mt-1">{instant(interval.recorded_at)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Closure recorded</dt>
                        <dd className="mt-1">{instant(interval.closed_recorded_at)}</dd>
                      </div>
                    </dl>
                  </article>
                ))
              )}
            </CardContent>
          </section>
        </Card>
        {canReadAnalytics ? (
          <div className="xl:col-span-2">
            <FeederTelemetry feederId={id} />
          </div>
        ) : null}
        <div className="xl:col-span-2">
          <Engineering feeder={feeder} canEdit={hasCapability(me, "catalog:write")} />
        </div>
      </div>
      <Dialog
        open={action !== null}
        onOpenChange={(open) => {
          if (!open && !mutation.isPending) setAction(null);
        }}
      >
        <DialogContent className="max-h-[90dvh] overflow-y-auto">
          {action ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {action.kind === "replace" ? "Replace assignment" : "Detach assignment"}
                </DialogTitle>
                <DialogDescription>
                  Only the selected assignment changes. Other assignments remain in place.
                </DialogDescription>
              </DialogHeader>
              <p>Old hardware: {hardwareLabel(action.source)}</p>
              {action.kind === "replace" ? (
                <>
                  <label className="block">
                    Search replacement controllers
                    <Input
                      type="search"
                      value={controllerSearch}
                      disabled={review}
                      onChange={(event) => {
                        setControllerSearch(event.target.value);
                        setControllerPage(1);
                        setTargetControllerId("");
                        setTargetKey("");
                      }}
                    />
                  </label>
                  {controllers.isLoading ? (
                    <p>Loading controllers…</p>
                  ) : controllers.error ? (
                    <p role="alert">
                      Could not load controllers.{" "}
                      <Button onClick={() => void controllers.refetch()}>Retry</Button>
                    </p>
                  ) : (
                    <>
                      <label className="block">
                        Replacement controller
                        <select
                          className="block w-full rounded border bg-background p-2"
                          value={targetControllerId}
                          disabled={review}
                          onChange={(event) => {
                            setTargetControllerId(event.target.value);
                            setTargetKey("");
                            setConfirmedMove(false);
                          }}
                        >
                          <option value="">Select a controller</option>
                          {(controllers.data?.items ?? [])
                            .filter(
                              (controller) =>
                                hasCapability(me, "catalog:write") ||
                                (me?.tenant_id != null && controller.tenant_id === me.tenant_id),
                            )
                            .map((controller) => (
                              <option key={controller.device_id} value={controller.device_id}>
                                {controller.display_name} ({controller.device_id})
                              </option>
                            ))}
                        </select>
                      </label>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          disabled={review || controllerPage <= 1}
                          onClick={() => {
                            setControllerPage(controllerPage - 1);
                            setTargetControllerId("");
                            setTargetKey("");
                          }}
                        >
                          Previous controllers
                        </Button>
                        <span>
                          Page {controllerPage} of{" "}
                          {Math.max(1, Math.ceil((controllers.data?.total ?? 0) / 25))}. Only owned
                          controllers are selectable.
                        </span>
                        <Button
                          variant="outline"
                          disabled={review || controllerPage * 25 >= (controllers.data?.total ?? 0)}
                          onClick={() => {
                            setControllerPage(controllerPage + 1);
                            setTargetControllerId("");
                            setTargetKey("");
                          }}
                        >
                          Next controllers
                        </Button>
                      </div>
                    </>
                  )}
                  {targetController.error ? (
                    <p role="alert">
                      Could not load replacement controller.{" "}
                      <Button onClick={() => void targetController.refetch()}>Retry</Button>
                    </p>
                  ) : null}
                  <label className="block">
                    Replacement meter
                    <select
                      className="block w-full rounded border bg-background p-2"
                      value={targetKey}
                      disabled={review}
                      onChange={(event) => {
                        setTargetKey(event.target.value);
                        setConfirmedMove(false);
                      }}
                    >
                      <option value="">Select a meter</option>
                      {hardware
                        .filter(
                          (item) =>
                            item.meter.controller_meter_id !==
                              action.source.meter.controller_meter_id &&
                            !(
                              item.meter.enabled &&
                              item.meter.source_mode === "feeder" &&
                              item.meter.feeder_id === id
                            ),
                        )
                        .map((item) => (
                          <option
                            key={`${item.controller.device_id}:${item.meter.controller_meter_id}`}
                            value={`${item.controller.device_id}:${item.meter.controller_meter_id}`}
                          >
                            {hardwareLabel(item)}
                          </option>
                        ))}
                    </select>
                  </label>
                  {target && !review ? (
                    <p>
                      New hardware: {hardwareLabel(target)}.{" "}
                      {!previousKnown
                        ? "Loading the previous feeder classification."
                        : isPlaceholder
                          ? "Currently in an unassigned placeholder."
                          : moving
                            ? `Currently assigned to ${previousFeeder?.feeder_name ?? `feeder #${target.meter.feeder_id}`}.`
                            : "No move from another real feeder is required."}
                    </p>
                  ) : null}
                  {previousFeederQuery.error ? (
                    <p role="alert">
                      Could not load the previous feeder.{" "}
                      <Button onClick={() => void previousFeederQuery.refetch()}>Retry</Button>
                    </p>
                  ) : null}
                  {moving && previousKnown && !review ? (
                    <label className="block">
                      <input
                        type="checkbox"
                        checked={confirmedMove}
                        onChange={(event) => setConfirmedMove(event.target.checked)}
                      />{" "}
                      Confirm move from{" "}
                      {previousFeeder?.feeder_name ?? `feeder #${target?.meter.feeder_id}`}
                    </label>
                  ) : null}
                </>
              ) : null}
              <label className="block">
                Effective time (
                {Intl.DateTimeFormat().resolvedOptions().timeZone || "local timezone"}). Leave blank
                for now.
                <Input
                  type="datetime-local"
                  value={effective}
                  disabled={review}
                  onChange={(event) => setEffective(event.target.value)}
                />
              </label>
              {invalidTime ? <p role="alert">Effective time must be in the past or now.</p> : null}
              <p>
                Saving changes cloud mappings and ratio snapshots. It does not queue or publish
                configuration, or confirm device application. Review and queue configuration
                separately for each affected controller.
              </p>
              <p>
                Saved old ratios: CT {action.source.meter.resolved_ct_primary ?? "Unknown"}:
                {action.source.meter.resolved_ct_secondary ?? "Unknown"}, VT{" "}
                {action.source.meter.resolved_vt_primary ?? "Unknown"}:
                {action.source.meter.resolved_vt_secondary ?? "Unknown"}.
                {action.kind === "replace"
                  ? ` Replacement ratios: CT ${(reviewedFeeder ?? feeder).ct_primary}:${(reviewedFeeder ?? feeder).ct_secondary}, VT ${(reviewedFeeder ?? feeder).vt_primary}:${(reviewedFeeder ?? feeder).vt_secondary}.`
                  : null}
              </p>
              {review ? (
                <p>
                  Review: {action.kind} {hardwareLabel(action.source)}
                  {reviewedTarget && action.kind === "replace"
                    ? ` with ${hardwareLabel(reviewedTarget)}`
                    : ""}
                  ; effective {parsedTime ? instant(parsedTime.toISOString()) : "now at save"}.
                </p>
              ) : null}
              {review && reviewedMove ? <p>Confirmed move from {reviewedPreviousFeeder}.</p> : null}
              {mutation.error ? (
                <p role="alert">
                  {mutation.error.message}
                  {mutation.error instanceof ApiError && mutation.error.status === 409
                    ? " Close and reopen this dialog to load a fresh review."
                    : ""}
                </p>
              ) : null}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={mutation.isPending}
                  onClick={() => (review ? setReview(false) : setAction(null))}
                >
                  {review ? "Back" : "Cancel"}
                </Button>
                {!review ? (
                  <Button
                    disabled={
                      invalidTime ||
                      (action.kind === "replace" &&
                        (!target || !previousKnown || (moving && !confirmedMove)))
                    }
                    onClick={() => {
                      setReviewedTarget(target ?? null);
                      setReviewedMove(moving);
                      setReviewedFeeder(feeder);
                      setReviewedPreviousFeeder(
                        previousFeeder?.feeder_name ?? `feeder #${target?.meter.feeder_id}`,
                      );
                      setReview(true);
                    }}
                  >
                    Review
                  </Button>
                ) : (
                  <Button
                    disabled={
                      mutation.isPending ||
                      (mutation.error instanceof ApiError && mutation.error.status === 409)
                    }
                    onClick={() => mutation.mutate()}
                  >
                    Confirm save
                  </Button>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </main>
  );
}

function Engineering({ feeder, canEdit }: { feeder: Feeder; canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  return (
    <Card>
      <section aria-labelledby="feeder-engineering-heading">
        <CardHeader className="flex-row items-center justify-between gap-3">
          <div>
            <CardTitle id="feeder-engineering-heading" className="text-base">
              Engineering and verification
            </CardTitle>
            <CardDescription className="mt-1">
              Technical ratings and evidence status.
            </CardDescription>
          </div>
          {canEdit ? <FeederEditDialog feeder={feeder} /> : null}
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <EngineeringValue
            label="Voltage and band"
            value={`${feeder.feeder_voltage || "Unknown"} · ${feeder.feeder_band || "Unknown"}`}
          />
          <EngineeringValue
            label="Transformer"
            value={`${feeder.power_transformer_id || "Unknown"} · ${feeder.power_transformer_capacity_mva ?? "Unknown"} MVA`}
          />
          <EngineeringValue
            label="CT ratio"
            value={`${feeder.ct_primary}:${feeder.ct_secondary}`}
            detail={`${feeder.ct_verification_state ?? "Unverified"} · ${feeder.ct_verification_source || "Evidence unknown"}`}
          />
          <EngineeringValue
            label="VT ratio"
            value={`${feeder.vt_primary}:${feeder.vt_secondary}`}
            detail={`${feeder.vt_verification_state ?? "Unverified"} · ${feeder.vt_verification_source || "Evidence unknown"}`}
          />
          <EngineeringValue
            label="Condition and station DC"
            value={feeder.ct_vt_condition || "Unknown"}
            detail={`${feeder.station_dc_nominal_v ?? "Unknown"} V nominal · ${feeder.station_dc_min_v ?? "Unknown"} to ${feeder.station_dc_max_v ?? "Unknown"} V`}
          />
          <EngineeringValue
            label="Network and installation"
            value={`${feeder.preferred_network || "Unknown"} · ${feeder.installation_type_available || "Unknown"}`}
          />
          <div className="sm:col-span-2 lg:col-span-3">
            <EngineeringValue label="Remarks" value={feeder.remarks || "None"} />
          </div>
          {canEdit ? (
            <RatioVerification
              values={feeder}
              ct={[feeder.ct_primary, feeder.ct_secondary]}
              vt={[feeder.vt_primary, feeder.vt_secondary]}
              onConfirm={(body) => api.verifyFeederRatio(feeder.feeder_id, body)}
              onVerified={async () => {
                await queryClient.cancelQueries({ queryKey: ["controller-config-preview"] });
                queryClient.removeQueries({ queryKey: ["controller-config-preview"] });
                await queryClient.invalidateQueries({ queryKey: ["feeder", feeder.feeder_id] });
              }}
            />
          ) : null}
        </CardContent>
      </section>
    </Card>
  );
}

function EngineeringValue({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}): JSX.Element {
  return (
    <div className="min-w-0 rounded-lg bg-muted/40 p-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-sm font-medium">{value}</p>
      {detail ? <p className="mt-1 break-words text-xs text-muted-foreground">{detail}</p> : null}
    </div>
  );
}

function localDateTime(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function FeederTelemetry({ feederId }: { feederId: number }): JSX.Element {
  const [end, setEnd] = useState(() => localDateTime(new Date()));
  const [start, setStart] = useState(() => localDateTime(new Date(Date.now() - 86_400_000)));
  const [range, setRange] = useState<{ start: string; end: string } | null>(null);
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();
  const valid =
    Number.isFinite(startTime) &&
    Number.isFinite(endTime) &&
    startTime < endTime &&
    endTime - startTime <= 32 * 86_400_000;
  const telemetry = useQuery({
    queryKey: ["feeder-telemetry", feederId, range],
    queryFn: () => api.feederTelemetry(feederId, range!.start, range!.end),
    enabled: range !== null,
  });
  return (
    <Card>
      <section aria-labelledby="feeder-telemetry-heading">
        <CardHeader>
          <CardTitle id="feeder-telemetry-heading" className="text-base">
            Feeder telemetry
          </CardTitle>
          <CardDescription>
            Measurement time, separate by source. Retained PostgreSQL data only, not a complete
            history. Up to 1,000 newest readings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
            <label className="block space-y-1 text-sm">
              Start ({Intl.DateTimeFormat().resolvedOptions().timeZone || "local timezone"})
              <Input
                className="min-h-11"
                type="datetime-local"
                value={start}
                onChange={(event) => setStart(event.target.value)}
              />
            </label>
            <label className="block space-y-1 text-sm">
              End (exclusive)
              <Input
                className="min-h-11"
                type="datetime-local"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
              />
            </label>
            <Button
              className="min-h-11"
              disabled={!valid}
              onClick={() =>
                setRange({
                  start: new Date(startTime).toISOString(),
                  end: new Date(endTime).toISOString(),
                })
              }
            >
              Load telemetry
            </Button>
          </div>
          {!valid ? (
            <p role="alert" className="text-sm text-destructive">
              Select a valid range of at most 32 days.
            </p>
          ) : null}
          {range && telemetry.isLoading ? (
            <p
              role="status"
              className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
            >
              Loading telemetry…
            </p>
          ) : null}
          {telemetry.error ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
              <p role="alert" className="text-sm">
                Could not load telemetry.
              </p>
              <Button
                variant="outline"
                className="min-h-11"
                onClick={() => void telemetry.refetch()}
              >
                Retry
              </Button>
            </div>
          ) : null}
          {telemetry.data ? (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[680px] text-left text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Measurement time</th>
                    <th className="px-3 py-2">Controller</th>
                    <th className="px-3 py-2">Device</th>
                    <th className="px-3 py-2">Meter</th>
                    <th className="px-3 py-2">Active power (W)</th>
                    <th className="px-3 py-2">Energy (Wh)</th>
                  </tr>
                </thead>
                <tbody>
                  {telemetry.data.map((point, index) => (
                    <tr
                      key={`${point.device_id}:${point.meter_id}:${point.device_timestamp_at}:${index}`}
                      className="border-t"
                    >
                      <td className="whitespace-nowrap px-3 py-2">
                        {instant(point.device_timestamp_at)}
                      </td>
                      <td className="px-3 py-2">{point.controller_id}</td>
                      <td className="px-3 py-2 font-mono text-xs">{point.device_id}</td>
                      <td className="px-3 py-2">{point.meter_id}</td>
                      <td className="px-3 py-2 tabular-nums">
                        {point.active_power_inst_x10 == null
                          ? "Unknown"
                          : point.active_power_inst_x10 / 10}
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {point.active_energy_tot_wh ?? "Unknown"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {telemetry.data.length === 0 ? (
                <p
                  role="status"
                  aria-label="No retained readings for this range."
                  className="p-4 text-sm text-muted-foreground"
                >
                  No retained readings for this range.
                </p>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </section>
    </Card>
  );
}
